import { BaseAPIClient } from '@mi-front/core';
import { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios';

let apiClient: BaseAPIClient | null = null;
let configuredApiRoot: string | undefined;

type GenerateUrlOpts = {
  prependBaseURL?: boolean;
  secure?: boolean;
  params?: object;
};

/** Registers the shared axios client used by generated Kubb API calls. */
export function setApiV2Client(client: BaseAPIClient, options: { apiRoot?: string } = {}) {
  apiClient = client;
  configuredApiRoot = options.apiRoot;
}

/** Returns the underlying transport client, throwing if bootstrap has not run. */
function requireApiClient() {
  if (!apiClient) {
    throw new Error('ApiV2 client not set. Call setApiV2Client() during app bootstrap.');
  }

  return apiClient;
}

/** Resolves api-v2 host; explicit per-request baseURL wins. */
function resolveApiV2BaseURL(options: { baseURL?: string } = {}) {
  return options.baseURL ?? configuredApiRoot ?? requireApiClient().getApiRoot();
}

/**
 * Builds a URL against the configured api-v2 root (e.g. api.mediainbox.io),
 * not the shared transport client's logger-api root.
 */
function generateApiV2Url(path: string, opts: GenerateUrlOpts = {}) {
  const client = requireApiClient();
  const shouldPrepend =
    opts.prependBaseURL !== false && !(path.startsWith('https://') || path.startsWith('http://'));

  const resolvedPath = shouldPrepend ? `${configuredApiRoot ?? client.getApiRoot()}${path}` : path;

  return client.generateUrl(resolvedPath, { ...opts, prependBaseURL: false });
}

/**
 * Issues a request against the api-v2 root even though the shared transport
 * axios instance defaults to logger-api (`VITE_API_ROOT`).
 */
function requestWithApiV2Root<T>(
  method: Method,
  url: string,
  options: AxiosRequestConfig = {},
): Promise<AxiosResponse<T>> {
  return requireApiClient().request<T>(method, url, {
    ...options,
    baseURL: resolveApiV2BaseURL(options),
  });
}

/**
 * Returns the axios client registered for api-v2 generated hooks and functions.
 * `generateUrl` and `request` are overridden so traffic targets the api-v2 root
 * instead of the shared transport client's logger-api root.
 */
export function getApiV2Client(): BaseAPIClient {
  const client = requireApiClient();

  return new Proxy(client, {
    get(target, prop, receiver) {
      if (prop === 'generateUrl') {
        return generateApiV2Url;
      }

      if (prop === 'request') {
        return requestWithApiV2Root;
      }

      const value = Reflect.get(target, prop, receiver);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

/** Subset of AxiosRequestConfig expected by Kubb-generated clients. */
export type RequestConfig<TVariables = unknown> = {
  baseURL?: string;
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS' | 'HEAD';
  url?: string;
  params?: unknown;
  data?: TVariables | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  headers?: AxiosRequestConfig['headers'];
};

/** Subset of AxiosResponse expected by Kubb-generated clients. */
export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers: AxiosResponse['headers'];
};

/** Error type used in Kubb-generated client and hook signatures. */
export type ResponseErrorConfig<TError = unknown> = AxiosError<TError>;

/**
 * Axios wrapper consumed by Kubb-generated clients.
 * Uses an explicit request or registered API root, otherwise the transport's configured root.
 */
export const axiosClient = async <TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<AxiosResponse<TData>> => {
  const baseURL = config.baseURL ?? configuredApiRoot;

  return getApiV2Client()
    .request<TData>(config.method as Method, config.url as string, {
      params: config.params,
      data: config.data,
      responseType: config.responseType,
      signal: config.signal,
      headers: config.headers,
      ...(baseURL ? { baseURL } : {}),
    })
    .catch((e: AxiosError<TError>) => {
      throw e;
    });
};

axiosClient.generateUrl = (url: string) => generateApiV2Url(url, { secure: true });

/** Client shape expected by Kubb-generated functions (`config.client` override). */
export type Client = typeof axiosClient;

export default axiosClient;
